import { ConfigProvider, Table } from 'antd'
import { TableGridProps } from './constants'
import './style.less'

/**
 * 表格网格组件
 *
 * 这是一个基于Ant Design Table封装的表格组件，提供了统一的表格样式和功能。
 * 支持自定义空状态、滚动配置、行选择等功能，适用于各种数据展示场景。
 *
 * @param className - 自定义CSS类名
 * @param tableInfo - 表格配置信息对象
 * @param emptyComponent - 自定义空状态组件
 *
 * 使用场景：
 * 1. 数据列表展示 - 用户列表、订单列表等
 * 2. 数据管理 - 增删改查操作的数据表格
 * 3. 报表展示 - 各种统计报表的数据展示
 * 4. 配置管理 - 系统配置项的管理表格
 * 5. 日志查看 - 系统日志、操作日志的查看
 * 6. 文件管理 - 文件列表的管理和操作
 * 7. 权限管理 - 用户权限、角色权限的管理
 * 8. 监控面板 - 系统监控数据的展示
 *
 * 使用示例：
 * ```tsx
 * // 基础用法
 * <TableGrid
 *   tableInfo={{
 *     tableData: dataSource,
 *     tableColumns: columns,
 *     getUniqueKey: (record) => record.id
 *   }}
 * />
 *
 * // 带行选择
 * <TableGrid
 *   tableInfo={{
 *     tableData: dataSource,
 *     tableColumns: columns,
 *     getUniqueKey: (record) => record.id,
 *     rowSelection: {
 *       onChange: (selectedRowKeys) => {
 *         console.log('选中的行:', selectedRowKeys)
 *       }
 *     }
 *   }}
 * />
 *
 * // 自定义空状态
 * <TableGrid
 *   tableInfo={tableInfo}
 *   emptyComponent={<div>暂无数据</div>}
 * />
 *
 * // 带滚动
 * <TableGrid
 *   tableInfo={{
 *     ...tableInfo,
 *     scrollHeight: 400,
 *     scrollWidth: 800
 *   }}
 * />
 * ```
 */
const TableGrid = ({ className = '', tableInfo, emptyComponent }: TableGridProps) => {
  // 解构表格配置信息
  const {
    tableData, // 表格数据源
    tableColumns, // 表格列配置
    getUniqueKey, // 获取唯一key的函数
    // tableIsLoading = false, // 表格加载状态（已注释）
    getTableChangeValues, // 表格变化回调函数
    rowSelection, // 行选择配置
    scrollHeight = undefined, // 垂直滚动高度
    scrollWidth = undefined, // 水平滚动宽度
    tableRef = undefined // 表格ref引用
  } = tableInfo

  /**
   * 处理表格变化事件（分页、排序、筛选）
   * 目前功能已注释，可根据需要启用
   * @param pagination - 分页信息
   * @param _filters - 筛选信息（未使用）
   * @param sorter - 排序信息
   */
  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    // 分页处理逻辑（已注释）
    // const { current: pageNo, pageSize } = pagination
    // let { columnKey: sortKey, order: sortType } = sorter
    // if (sortType) {
    //   sortType = sortType === 'descend' ? 'desc' : 'asc'
    // }
    // if (getTableChangeValues) {
    //   getTableChangeValues({ pageNo, pageSize, sortKey, sortType })
    // }
  }

  return (
    <div className={`lenovo-table-container ${className}`}>
      <ConfigProvider renderEmpty={() => emptyComponent}>
        <Table
          ref={tableRef}
          columns={tableColumns}
          dataSource={tableData}
          bordered={false}
          rowKey={(record: any) => getUniqueKey(record)}
          scroll={{ x: scrollWidth, y: scrollHeight }}
          onChange={handleTableChange}
          rowSelection={rowSelection}
          className="lenovo-common-table"
          pagination={false}
        />
      </ConfigProvider>
    </div>
  )
}
export default TableGrid
